// src/index.ts
import {
  canonicalizeQr20Json,
  validateQr20Envelope,
  hashQr20Payload,
  Qr20Error
} from "@qr20/envelope";
var QR20_WEBRTC_CHANNEL_LABEL = "qr20";
var Qr20TransportWebrtcError = class extends Qr20Error {
  code;
  constructor(code, message, options = {}) {
    super(message, options);
    this.name = "Qr20TransportWebrtcError";
    this.code = code;
  }
};
function createQr20WebrtcChannel(pc, options = {}) {
  const init = {
    ordered: true,
    protocol: "qr20"
  };
  if (options.id !== void 0) {
    init.id = options.id;
    init.negotiated = options.negotiated ?? true;
  } else if (options.negotiated !== void 0) {
    init.negotiated = options.negotiated;
  }
  return pc.createDataChannel(QR20_WEBRTC_CHANNEL_LABEL, init);
}
function detectQr20WebrtcChannel(channel) {
  return channel.label === QR20_WEBRTC_CHANNEL_LABEL && channel.ordered === true && (channel.maxRetransmits === null || channel.maxRetransmits === void 0) && (channel.maxPacketLifeTime === null || channel.maxPacketLifeTime === void 0);
}
function assertQr20WebrtcChannel(channel) {
  if (channel.label !== QR20_WEBRTC_CHANNEL_LABEL) {
    return;
  }
  if (channel.ordered !== true) {
    throw new Qr20TransportWebrtcError(
      "TRANSPORT_WEBRTC_UNRELIABLE_QR20_CHANNEL",
      "qr20 data channel must be ordered (W-A)",
      {
        hint: 'Inbound channel labelled "qr20" has ordered=false.',
        remediation: "Reject the channel and close the peer connection."
      }
    );
  }
  if (channel.maxRetransmits != null || channel.maxPacketLifeTime != null) {
    throw new Qr20TransportWebrtcError(
      "TRANSPORT_WEBRTC_UNRELIABLE_QR20_CHANNEL",
      "qr20 data channel must be fully reliable (W-A)",
      {
        hint: 'Inbound channel labelled "qr20" has maxRetransmits or maxPacketLifeTime set.',
        remediation: "Reject the channel; QR20 requires reliable + ordered delivery."
      }
    );
  }
}
function encodeQr20WebrtcMessage(envelope) {
  const json = canonicalizeQr20Json(envelope);
  return new TextEncoder().encode(json);
}
async function decodeQr20WebrtcMessage(payload, crypto) {
  const bytes = payload instanceof Uint8Array ? payload : new Uint8Array(payload);
  let text;
  try {
    text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch (cause) {
    throw new Qr20TransportWebrtcError(
      "TRANSPORT_WEBRTC_INVALID_FRAME",
      "qr20 webrtc payload is not valid UTF-8",
      { cause }
    );
  }
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch (cause) {
    throw new Qr20TransportWebrtcError(
      "TRANSPORT_WEBRTC_INVALID_FRAME",
      "qr20 webrtc payload is not valid JSON",
      { cause }
    );
  }
  const parsedRecord = parsed;
  const strippedForHash = { ...parsedRecord };
  delete strippedForHash.proof;
  if (strippedForHash.id !== void 0) {
    delete strippedForHash.id;
  }
  if (typeof strippedForHash.creator === "object" && strippedForHash.creator !== null) {
    strippedForHash.creator = { ...strippedForHash.creator, signature: "" };
  }
  const contentIdRecomputed = await hashQr20Payload(strippedForHash, {
    crypto,
    algorithm: "SHA-256"
  });
  const expectedContentId = `sha256:${contentIdRecomputed}`;
  if (parsedRecord.id !== expectedContentId) {
    throw new Qr20TransportWebrtcError(
      "TRANSPORT_WEBRTC_CONTENT_ID_MISMATCH",
      `content_id mismatch: expected ${expectedContentId}, got ${String(parsedRecord.id)}`,
      {
        hint: "The envelope content_id does not match the recomputed hash of its canonical bytes.",
        remediation: "Discard the frame; the payload was tampered with or the producer has a canonicalisation bug."
      }
    );
  }
  const validationResult = validateQr20Envelope(parsed);
  if (!validationResult.ok) {
    const first = validationResult.errors[0];
    const detail = first ? `${first.path}: ${first.message}` : "unknown validation failure";
    throw new Qr20TransportWebrtcError(
      "TRANSPORT_WEBRTC_INVALID_FRAME",
      `qr20 webrtc payload failed envelope validation: ${detail}`,
      {
        hint: "The binary payload decoded as JSON but does not conform to the QR20 envelope schema.",
        remediation: "Discard the frame; the peer sent a malformed envelope."
      }
    );
  }
  return validationResult.value;
}
function decodeQr20WebrtcMessageSync(payload) {
  const bytes = payload instanceof Uint8Array ? payload : new Uint8Array(payload);
  let text;
  try {
    text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch (cause) {
    throw new Qr20TransportWebrtcError(
      "TRANSPORT_WEBRTC_INVALID_FRAME",
      "qr20 webrtc payload is not valid UTF-8",
      { cause }
    );
  }
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch (cause) {
    throw new Qr20TransportWebrtcError(
      "TRANSPORT_WEBRTC_INVALID_FRAME",
      "qr20 webrtc payload is not valid JSON",
      { cause }
    );
  }
  const validationResult = validateQr20Envelope(parsed);
  if (!validationResult.ok) {
    const first = validationResult.errors[0];
    const detail = first ? `${first.path}: ${first.message}` : "unknown validation failure";
    throw new Qr20TransportWebrtcError(
      "TRANSPORT_WEBRTC_INVALID_FRAME",
      `qr20 webrtc payload failed envelope validation: ${detail}`,
      {
        hint: "The binary payload decoded as JSON but does not conform to the QR20 envelope schema.",
        remediation: "Discard the frame; the peer sent a malformed envelope."
      }
    );
  }
  return validationResult.value;
}
var Qr20WebrtcSession = class {
  channel;
  cryptoSubtle;
  backpressureCallbacks = /* @__PURE__ */ new Set();
  closed = false;
  constructor(channel, options) {
    this.channel = channel;
    this.cryptoSubtle = options.crypto.subtle;
    this.channel.binaryType = "arraybuffer";
    const threshold = options.bufferedAmountLowThreshold ?? 65536;
    this.channel.bufferedAmountLowThreshold = threshold;
    this.channel.addEventListener("bufferedamountlow", () => {
      for (const cb of this.backpressureCallbacks) {
        cb();
      }
    });
  }
  async send(envelope) {
    if (this.closed) {
      throw new Qr20TransportWebrtcError(
        "TRANSPORT_WEBRTC_BACKPRESSURE_DROP",
        "Cannot send on a closed session",
        { hint: "The session has been closed." }
      );
    }
    const bytes = encodeQr20WebrtcMessage(envelope);
    console.log("[qr20-transport] send", { bytes: bytes.byteLength, channelState: this.channel.readyState, id: envelope?.id });
    this.channel.send(bytes);
    if (this.channel.bufferedAmount > this.channel.bufferedAmountLowThreshold) {
      await new Promise((resolve) => {
        const unsub = this.onBackpressureLow(() => {
          unsub();
          resolve();
        });
      });
    }
  }
  async *receive() {
    const queue = [];
    let resolve = null;
    let done = false;
    const onMessage = (event) => {
      const msgEvent = event;
      if (typeof msgEvent.data === "string") {
        console.warn("[qr20-transport] received text frame, ignoring");
        return;
      }
      const dataLen = msgEvent.data instanceof ArrayBuffer ? msgEvent.data.byteLength : msgEvent.data.byteLength;
      console.log("[qr20-transport] message event", { bytes: dataLen });
      decodeQr20WebrtcMessage(
        msgEvent.data,
        { subtle: this.cryptoSubtle }
      ).then((envelope) => {
        console.log("[qr20-transport] decoded envelope", { id: envelope?.id });
        queue.push(envelope);
        if (resolve) {
          resolve();
          resolve = null;
        }
      }).catch((err) => {
        console.warn("[qr20-transport] Frame decode failed:", err instanceof Error ? err.message : String(err));
      });
    };
    const onClose = () => {
      done = true;
      if (resolve) {
        resolve();
        resolve = null;
      }
    };
    this.channel.addEventListener("message", onMessage);
    this.channel.addEventListener("close", onClose);
    try {
      while (!done || queue.length > 0) {
        if (queue.length > 0) {
          yield queue.shift();
        } else if (!done) {
          await new Promise((r) => {
            resolve = r;
          });
        }
      }
    } finally {
      this.channel.removeEventListener("message", onMessage);
      this.channel.removeEventListener("close", onClose);
    }
  }
  bufferedAmount() {
    return this.channel.bufferedAmount;
  }
  onBackpressureLow(callback) {
    this.backpressureCallbacks.add(callback);
    return () => {
      this.backpressureCallbacks.delete(callback);
    };
  }
  async close(reason) {
    if (this.closed) return;
    this.closed = true;
    this.channel.close();
    if (this.channel.readyState !== "closed") {
      await new Promise((resolve) => {
        const onClose = () => {
          this.channel.removeEventListener("close", onClose);
          resolve();
        };
        this.channel.addEventListener("close", onClose);
        setTimeout(() => {
          this.channel.removeEventListener("close", onClose);
          resolve();
        }, 1e3);
      });
    }
    void reason;
  }
};
export {
  QR20_WEBRTC_CHANNEL_LABEL,
  Qr20TransportWebrtcError,
  Qr20WebrtcSession,
  assertQr20WebrtcChannel,
  createQr20WebrtcChannel,
  decodeQr20WebrtcMessage,
  decodeQr20WebrtcMessageSync,
  detectQr20WebrtcChannel,
  encodeQr20WebrtcMessage
};
